import { useEffect, useRef } from 'react';

/**
 * useTouchGesture(ref, options)
 *
 * Attaches touch event listeners to the element at `ref` and fires
 * callbacks for detected gestures.  All listeners are passive unless
 * the gesture requires preventDefault (pinch, double-tap on SVGs).
 *
 * Supported callbacks (all optional):
 *   onSwipeLeft(velocity)   — horizontal swipe left  (velocity px/ms)
 *   onSwipeRight(velocity)  — horizontal swipe right
 *   onSwipeUp(velocity)     — vertical swipe up
 *   onSwipeDown(velocity)   — vertical swipe down
 *   onPinch(scale, midX, midY) — two-finger pinch (scale < 1 = zoom out)
 *   onDoubleTap(x, y)       — double tap with page coordinates
 *   onLongPress(x, y)       — 500ms long press
 *
 * Options:
 *   threshold        {number}  min px for a swipe to register (default 40)
 *   velocityThreshold {number} min px/ms for swipe (default 0.1)
 *   touchAction      {string}  applied to element.style.touchAction (default 'pan-y')
 */
const useTouchGesture = (ref, {
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  onPinch,
  onDoubleTap,
  onLongPress,
  threshold         = 40,
  velocityThreshold = 0.1,
  touchAction       = 'pan-y',
} = {}) => {

  // Keep callbacks in a ref so the stable event handler always sees current values
  const cbRef = useRef({});
  cbRef.current = { onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onPinch, onDoubleTap, onLongPress };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (touchAction) el.style.touchAction = touchAction;

    // ── Per-gesture tracking state ────────────────────────────────────────
    let t1 = null;  // { x, y, t } for first touch start
    let pinchStartDist  = null;
    let pinchBaseMidX   = 0;
    let pinchBaseMidY   = 0;
    let lastTapTime     = 0;
    let lastTapX        = 0;
    let lastTapY        = 0;
    let longPressTimer  = null;
    let moved           = false;

    const clearLongPress = () => { if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; } };

    const dist2 = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const mid   = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

    const onStart = (e) => {
      moved = false;

      if (e.touches.length === 2) {
        clearLongPress();
        pinchStartDist = dist2(e.touches[0], e.touches[1]);
        const m = mid(e.touches[0], e.touches[1]);
        pinchBaseMidX = m.x;
        pinchBaseMidY = m.y;
        t1 = null;
        return;
      }

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        t1 = { x: touch.clientX, y: touch.clientY, t: Date.now() };
        pinchStartDist = null;

        // Long press timer
        const tx = touch.clientX, ty = touch.clientY;
        longPressTimer = setTimeout(() => {
          if (!moved) cbRef.current.onLongPress?.(tx, ty);
          longPressTimer = null;
        }, 500);
      }
    };

    const onMove = (e) => {
      if (e.touches.length === 2 && pinchStartDist !== null && cbRef.current.onPinch) {
        e.preventDefault();
        const newDist = dist2(e.touches[0], e.touches[1]);
        const scale   = newDist / pinchStartDist;
        cbRef.current.onPinch(scale, pinchBaseMidX, pinchBaseMidY);
        return;
      }

      if (t1 && e.touches.length === 1) {
        const dx = Math.abs(e.touches[0].clientX - t1.x);
        const dy = Math.abs(e.touches[0].clientY - t1.y);
        if (dx > 5 || dy > 5) { moved = true; clearLongPress(); }
      }
    };

    const onEnd = (e) => {
      clearLongPress();
      pinchStartDist = null;

      if (!t1 || e.changedTouches.length === 0) { t1 = null; return; }

      const touch = e.changedTouches[0];
      const dx    = touch.clientX - t1.x;
      const dy    = touch.clientY - t1.y;
      const dt    = Math.max(1, Date.now() - t1.t);
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Double tap detection (within 300ms, within 40px)
      if (!moved) {
        const now = Date.now();
        if (now - lastTapTime < 300 && Math.hypot(touch.clientX - lastTapX, touch.clientY - lastTapY) < 40) {
          cbRef.current.onDoubleTap?.(touch.clientX, touch.clientY);
          lastTapTime = 0;
        } else {
          lastTapTime = now;
          lastTapX = touch.clientX;
          lastTapY = touch.clientY;
        }
      }

      t1 = null;

      if (moved && absDx >= threshold && absDx > absDy) {
        const vx = absDx / dt;
        if (vx >= velocityThreshold) {
          dx < 0
            ? cbRef.current.onSwipeLeft?.(vx)
            : cbRef.current.onSwipeRight?.(vx);
        }
      } else if (moved && absDy >= threshold && absDy > absDx) {
        const vy = absDy / dt;
        if (vy >= velocityThreshold) {
          dy < 0
            ? cbRef.current.onSwipeUp?.(vy)
            : cbRef.current.onSwipeDown?.(vy);
        }
      }
    };

    const onCancel = () => { clearLongPress(); t1 = null; pinchStartDist = null; };

    el.addEventListener('touchstart',  onStart,  { passive: true });
    el.addEventListener('touchmove',   onMove,   { passive: false });
    el.addEventListener('touchend',    onEnd,    { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });

    return () => {
      if (touchAction) el.style.touchAction = '';
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onCancel);
      clearLongPress();
    };
  }, [ref, threshold, velocityThreshold, touchAction]);
};

export default useTouchGesture;
